/**
 * types.ts — Wire types locais para Gestão de Usuários (Brief 5 A1, W3.1).
 *
 * Decisão executiva W3.1-D1: wire types locais (padrão Fatia G / PR #26 /
 * PR #45 / PR #64). Espelha SerializedUser + SerializedInviteToken + HierarchyNode
 * do Engine (apps/engine/src/modules/users/users.serialization.ts) sem importar
 * diretamente — mantém atendimento desacoplado dos tipos Prisma do engine.
 */

export interface UserWireItem {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  primaryRole: string;
  additionalCapabilities: string[];
  status: string; // 'active' | 'archived' | 'suspended' | 'on_leave' | 'inactive'
  mfaEnabled: boolean;
  mfaPending: boolean;
  handoffDefaultMode: string;
  mobilePreferences: Record<string, unknown>;
  supervisorId: string | null;
  teamId: string | null;
  reportsToUserId: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  archivedAt: string | null;
  archivedReason: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  onLeaveFrom: string | null;
  onLeaveUntil: string | null;
  version: number;
}

export interface InviteTokenWireItem {
  id: string;
  tenantId: string;
  email: string;
  primaryRole: string;
  expiresAt: string;
  status: string; // 'pending' | 'accepted' | 'expired' | 'revoked'
  createdByUserId: string;
  acceptedByUserId: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface HierarchyNodeWire {
  user: UserWireItem;
  directReports: UserWireItem[];
  managersChain: UserWireItem[];
}

export interface ListUsersResponse {
  data: UserWireItem[];
  nextCursor: string | null;
  total: number;
}

export interface ListUsersParams {
  cursor?: string;
  limit?: number;
  status?: string;
  role?: string;
  /**
   * Casa `name` ∪ `email`, insensitive. O parâmetro EXISTIA no engine
   * (`users.service.ts:74-79`) e faltava aqui — adicionado na frente do
   * Localizador Universal (⌘K).
   */
  search?: string;
}

export interface CreateUserBody {
  email: string;
  name: string;
  primaryRole: string;
  reportsToUserId?: string | null;
}

export interface UpdateUserBody {
  name?: string;
  primaryRole?: string;
  reportsToUserId?: string | null;
  additionalCapabilities?: string[];
}

export interface DeactivateUserBody {
  reason: string;
}

export interface InviteUserBody {
  email: string;
  primaryRole: string;
}

/** Aceite de convite (04/Set/2026) — 201 do convite e do reenvio: o segredo volta UMA vez. */
export interface InviteEmailDelivery {
  status: 'sent' | 'skipped' | 'failed';
  reason?: string;
}
export interface InviteCreatedWire extends InviteTokenWireItem {
  acceptSecret: string;
  emailDelivery: InviteEmailDelivery;
}

/** `GET /v1/auth/invite/:secret` — o que a página pública de aceite mostra. */
export interface InvitePreviewWire {
  email: string;
  primaryRole: string;
  tenantName: string;
  tenantSlug: string;
  expiresAt: string;
}

export type UserStatus = 'active' | 'archived' | 'suspended' | 'on_leave' | 'inactive';
export type InviteTokenStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export const USER_ROLE_LABELS: Record<string, string> = {
  atendente: 'Atendente',
  supervisor: 'Supervisor',
  gestor: 'Gestor',
  admin_tenant: 'Admin',
  dpo_tenant: 'DPO',
  suporte_lume: 'Suporte Lume',
  engenharia_lume: 'Engenharia Lume',
  direcao_lume: 'Direção Lume',
};

export const USER_STATUS_LABELS: Record<string, string> = {
  active: 'Ativo',
  archived: 'Desativado',
  suspended: 'Suspenso',
  on_leave: 'Em licença',
  inactive: 'Inativo',
};

/** «Esqueci minha senha» (Item 477). */
export interface PasswordResetPreviewWire {
  email: string;
  tenantName: string;
  tenantSlug: string;
  expiresAt: string;
}
export interface PasswordResetLinkWire {
  resetSecret: string;
  expiresAt: string;
  email: string;
}
